import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import i18n from "../i18n";
import API from "./api";

const AuthContext = createContext(null);

/**
 * Resolve a friendly display name for the user. Priority:
 *   1. user.name (set during registration or via owner-profile sync)
 *   2. The local part of the email (before @)
 *   3. Generic "there" fallback
 */
export function displayName(user) {
  if (!user) return "";
  const n = (user.name || "").trim();
  if (n) return n;
  const email = (user.email || "").trim();
  if (email.includes("@")) {
    const local = email.split("@")[0].replace(/[._-]+/g, " ").trim();
    if (local) return local.charAt(0).toUpperCase() + local.slice(1);
  }
  return "there";
}

/**
 * Show a "Welcome back, {{name}}" toast. Safe to call multiple times — Sonner
 * deduplicates on identical id within a short window. We use a stable id so
 * rapid auth refreshes (e.g., useEffect strict-mode) don't fire twice.
 */
function showWelcomeToast(user, mode = "login") {
  if (!user) return;
  const name = displayName(user);
  const title = i18n.t("auth.welcome_toast_title", { name, defaultValue: `Welcome back, ${name}` });
  const description = mode === "register"
    ? i18n.t("auth.welcome_toast_register", { defaultValue: "Your DONiTSH!T account is ready." })
    : i18n.t("auth.welcome_toast_login", { defaultValue: "Glad to see you again." });
  toast.success(title, { id: `welcome-${user.user_id || user.email}`, description });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 1) Pick up session_id from Emergent Auth redirect hash
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (sid) {
        API.post("/auth/google/session", { session_id: sid })
          .then(({ data }) => {
            if (data?.session_token) localStorage.setItem("donitshit_token", data.session_token);
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
            refresh().then((u) => { if (u) showWelcomeToast(u, "login"); });
          })
          .catch(() => refresh());
        return;
      }
    }
    refresh();
  }, [refresh]);

  const loginEmail = async (email, password) => {
    const { data } = await API.post("/auth/email/login", { email, password });
    if (data?.session_token) localStorage.setItem("donitshit_token", data.session_token);
    const fresh = await refresh();
    if (fresh) showWelcomeToast(fresh, "login");
    return data.user;
  };

  const registerEmail = async (email, password, name, language, country) => {
    const { data } = await API.post("/auth/email/register", { email, password, name, language, country });
    if (data?.session_token) localStorage.setItem("donitshit_token", data.session_token);
    const fresh = await refresh();
    if (fresh) showWelcomeToast(fresh, "register");
    return data.user;
  };

  const logout = async ({ silent = false } = {}) => {
    try {
      await API.post("/auth/logout");
    } catch (e) {
      // ignore network errors — the local session is already going to be cleared
    }
    localStorage.removeItem("donitshit_token");
    setUser(null);
    if (!silent) {
      toast.success(
        i18n.t("auth.logout_toast_title", { defaultValue: "You\u2019ve been signed out" }),
        {
          id: "logout-success",
          description: i18n.t("auth.logout_toast_sub", { defaultValue: "Come back anytime." }),
        }
      );
    }
  };

  // Google sign-in. Auto-detects whether direct OAuth (user's own GOOGLE_CLIENT_ID)
  // is configured on the backend; falls back to Emergent-managed auth otherwise.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = async (nextPath = "/profile") => {
    try {
      const { data } = await API.get("/auth/google/config");
      if (data?.direct_oauth_configured) {
        const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const next = encodeURIComponent(nextPath || "/profile");
        window.location.href = `${base}/api/auth/google/start?next=${next}`;
        return;
      }
    } catch {
      // fall through to Emergent flow
    }
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh, loginEmail, registerEmail, logout, startGoogle, displayName: () => displayName(user) }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
